import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { Desk } from 'app/components/commons/Hotel/Spaces/Desk/Desk';
import { Space } from 'app/components/commons/Hotel/Spaces/Space/Space';
import { SpaceModal } from 'app/components/commons/Hotel/Spaces/SpaceModal/SpaceModal';
import {
  type SpacesFormData,
  formatNewSpaces,
  formatSpacesFormFromAPI,
  reformatSpacesFormForAPI,
} from 'app/components/commons/Hotel/Spaces/utils';
import type { SpaceSubmitType } from 'app/components/commons/Hotel/Types';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { HeaderWrapper } from 'app/components/pages/Extranet/HotelBuilder/HeaderWrapper/HeaderWrapper';
import { hotelPath } from 'app/hooks/data/hotels/useHotel';
import { useSaveHotelSpaces } from 'app/hooks/data/hotels/useHotelSpaces';
import type { Hotel } from 'app/typings/builder/hotel';
import type { SpaceType } from 'app/typings/builder/spaceType';

import './SpacesForm.scss';

export const SpacesForm: FC<Props> = ({
  spaceTypes,
  hotel,
  onSubmit,
  isLoading = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { mutate: saveSpaces } = useSaveHotelSpaces(hotel.id.toString(), {
    showSuccessMessage: false,
  });

  const onFinish = useCallback(
    (values: SpacesFormData) => {
      const reformatted = reformatSpacesFormForAPI(values, hotel, spaceTypes);

      saveSpaces(reformatted as unknown as SpaceSubmitType, {
        onSuccess: () =>
          queryClient.invalidateQueries([hotelPath, { hotelId: hotel.id }]),
      });
      onSubmit();
    },
    [spaceTypes, hotel, saveSpaces, onSubmit, queryClient]
  );

  const formattedValues = useMemo(() => {
    if (!hotel) {
      return {};
    }

    return formatSpacesFormFromAPI(hotel, spaceTypes);
  }, [hotel, spaceTypes]);

  const onModalSave = (newSpaces: number[]) => {
    const values = form.getFieldsValue();
    const formatted = formatNewSpaces(newSpaces, values, spaceTypes, false);

    form.setFieldsValue(formatted);
  };

  return (
    <HeaderWrapper
      nextDisabled={false}
      handleNext={() => form.submit()}
      isLoading={isLoading}
    >
      <FormLegacyRenderProp
        form={form}
        onFinish={onFinish}
        initialValues={formattedValues}
        className="spaces-form"
      >
        {(values, { setFieldsValue, validateFields }) => (
          <div className="form-container">
            <SCard title="Desk">
              <Desk values={values} />
            </SCard>
            <Form.List name="spaces">
              {(fields, { remove }) => (
                <>
                  {fields.map((field, idx) => {
                    const space = values.spaces[idx];
                    const spaceType = spaceTypes.find(
                      (type) => type.id === space.spaceTypeId
                    );

                    if (!space || !spaceType) {
                      return null;
                    }

                    return (
                      <SCard
                        key={field.key}
                        title={spaceType.name}
                        rightHeader={
                          <div
                            onClick={() => remove(idx)}
                            className="card-delete"
                          >
                            <DeleteOutlined />
                          </div>
                        }
                      >
                        <Space
                          values={values}
                          field={field}
                          fieldIndex={idx}
                          spaceType={spaceType}
                          setFieldsValue={setFieldsValue}
                          hotelId={hotel.id}
                          picturesCategories={[
                            { type: 'official', name: 'Media gallery' },
                          ]}
                          kind="official"
                          previousPrefixes={['spaces']}
                          validateFields={validateFields}
                        />
                      </SCard>
                    );
                  })}
                  <div className="spaces__add-button-container">
                    <div
                      onClick={() => setIsModalOpen(true)}
                      className="spaces__add-button"
                    >
                      <PlusOutlined />
                      <span className="text">Add spaces</span>
                    </div>
                    <SpaceModal
                      onCancel={() => setIsModalOpen(false)}
                      onSave={onModalSave}
                      visible={isModalOpen}
                      values={values}
                      spaceTypes={spaceTypes}
                    />
                  </div>
                </>
              )}
            </Form.List>
          </div>
        )}
      </FormLegacyRenderProp>
    </HeaderWrapper>
  );
};

type Props = {
  spaceTypes: SpaceType[];
  hotel: Hotel;
  onSubmit: () => void;
  isLoading?: boolean;
};
